import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuthStore } from '../store/auth.store'
import { api } from '../services/api'
import { queryClient } from '../main'

const ROOM_VENUE_TYPES = new Set(['HOTEL', 'APARTMENT', 'HOSPITAL'])
const PLAN_ORDER: Record<string, number> = { BASIC: 0, PRO: 1, ENTERPRISE: 2 }

type NavItem = { to: string; label: string | null; icon: string; venueOnly: string | null }

const NAV_GROUPS: { label: string | null; requiredPlan: string | null; items: NavItem[] }[] = [
  {
    label: null,
    requiredPlan: null,
    items: [
      { to: '/dashboard',    label: 'Dashboard',    icon: '📊', venueOnly: null },
      { to: '/menu',         label: 'Menú',          icon: '🍽', venueOnly: null },
      { to: '/tables',       label: null,            icon: '🪑', venueOnly: null },
      { to: '/staff',        label: 'Personal',      icon: '👥', venueOnly: null },
      { to: '/orders',       label: 'Órdenes',       icon: '📋', venueOnly: null },
      { to: '/event',        label: 'Evento',        icon: '🎉', venueOnly: 'EVENT_MODE' },
      { to: '/settings',     label: 'Configuración', icon: '⚙️', venueOnly: null },
    ],
  },
  {
    label: null,
    requiredPlan: 'PRO',
    items: [
      { to: '/loyalty',  label: 'Lealtad',      icon: '⭐', venueOnly: null },
      { to: '/ratings',  label: 'Evaluaciones', icon: '★',  venueOnly: null },
    ],
  },
  {
    label: 'ENTERPRISE',
    requiredPlan: 'ENTERPRISE',
    items: [
      { to: '/reservations', label: 'Reservaciones', icon: '📅', venueOnly: 'RESTAURANT' },
    ],
  },
]

export default function Layout() {
  const { restaurantName, userName, clearAuth, superAdminToken, exitImpersonation } = useAuthStore()
  const navigate = useNavigate()
  const isImpersonating = !!superAdminToken

  const { data: restaurant } = useQuery<{
    venueType: string
    reservationsEnabled: boolean
    eventModeActive: boolean
    plan: string
  }>({
    queryKey: ['restaurant-settings'],
    queryFn: async () => (await api.get('/restaurant')).data,
    staleTime: 5 * 60 * 1000,
  })

  const isRoomVenue = ROOM_VENUE_TYPES.has(restaurant?.venueType ?? '')
  const currentPlanOrder = PLAN_ORDER[restaurant?.plan ?? 'BASIC'] ?? 0

  function isItemVisible(item: NavItem): boolean {
    if (item.venueOnly === null) return true
    if (item.to === '/reservations') {
      return restaurant?.venueType === 'RESTAURANT' && (restaurant?.reservationsEnabled ?? false)
    }
    if (item.to === '/event') return restaurant?.venueType === 'EVENT' || restaurant?.eventModeActive === true
    return restaurant?.venueType === item.venueOnly
  }

  function resolveLabel(item: NavItem): string {
    if (item.to === '/tables') return isRoomVenue ? 'Cuartos' : 'Mesas'
    return item.label ?? ''
  }

  function handleLogout() {
    clearAuth()
    queryClient.clear()
    navigate('/login')
  }

  function handleExitImpersonation() {
    exitImpersonation()
    queryClient.clear()
    navigate('/superadmin')
  }

  return (
    <div className="flex h-screen bg-gray-50 font-sans">
      {/* Sidebar */}
      <aside className="w-56 bg-white border-r border-gray-100 flex flex-col shrink-0">
        {/* Logo */}
        <div className="px-6 py-5 border-b border-gray-100">
          <span className="text-2xl font-black tracking-widest text-gray-900">TEFFY</span>
          <p className="text-xs text-gray-400 mt-0.5 truncate">{restaurantName}</p>
        </div>

        {/* Nav */}
        <nav className="flex-1 p-3 space-y-3 overflow-y-auto">
          {NAV_GROUPS.map((group, gi) => {
            const planUnlocked = group.requiredPlan === null || currentPlanOrder >= (PLAN_ORDER[group.requiredPlan] ?? 99)
            const visibleItems = group.items.filter(isItemVisible)

            // Para grupos de plan superior con items condicionados a venueOnly (ej. reservaciones)
            // si ningún item sería visible por venue, ocultar el grupo entero
            const itemsToShow = planUnlocked
              ? visibleItems
              : group.items // bloqueados: mostrar todos para indicar que existen

            if (itemsToShow.length === 0) return null

            return (
              <div key={gi} className="space-y-1">
                {group.label && (
                  <p className={`px-3 pt-1 pb-0.5 text-[10px] font-bold uppercase tracking-widest ${
                    planUnlocked ? 'text-gray-400' : 'text-gray-300'
                  }`}>
                    {group.label}
                  </p>
                )}
                {itemsToShow.map((item) => {
                  const label = resolveLabel(item)
                  if (planUnlocked) {
                    return (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        className={({ isActive }) =>
                          `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                            isActive ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'
                          }`
                        }
                      >
                        <span className="text-base">{item.icon}</span>
                        {label}
                      </NavLink>
                    )
                  }

                  // Item bloqueado por plan
                  return (
                    <div
                      key={item.to}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-300 cursor-default select-none"
                      title={`Requiere plan ${group.requiredPlan}`}
                    >
                      <span className="text-base opacity-40">{item.icon}</span>
                      <span className="flex-1">{label}</span>
                      <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${
                        group.requiredPlan === 'ENTERPRISE'
                          ? 'bg-purple-50 text-purple-300'
                          : 'bg-blue-50 text-blue-300'
                      }`}>
                        {group.requiredPlan}
                      </span>
                    </div>
                  )
                })}
              </div>
            )
          })}
        </nav>

        {/* User */}
        <div className="p-4 border-t border-gray-100">
          <p className="text-xs text-gray-500 truncate mb-2">{userName}</p>
          <button
            onClick={handleLogout}
            className="w-full text-xs text-gray-400 hover:text-red-500 text-left transition-colors"
          >
            Cerrar sesión →
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {isImpersonating && (
          <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center justify-between text-sm shrink-0">
            <span className="text-amber-800">
              🔍 Viendo como administrador de <strong>{restaurantName}</strong> (modo superadmin)
            </span>
            <button
              onClick={handleExitImpersonation}
              className="font-semibold text-amber-900 hover:underline"
            >
              ← Volver a SuperAdmin
            </button>
          </div>
        )}
        <div className="flex-1 overflow-y-auto">
          <Outlet />
        </div>
      </main>
    </div>
  )
}
